import { computed, inject } from '@angular/core';
import { signalStore, withState, withComputed, withMethods, patchState } from '@ngrx/signals';
import { NotificationInboxService } from '../services/notification-inbox.service';
import { InboxNotification } from '../notification.model';

interface InboxState {
  items: InboxNotification[];
  unread: number;
  loading: boolean;
  loaded: boolean;
}

const initial: InboxState = { items: [], unread: 0, loading: false, loaded: false };

/**
 * Inbox state for the header bell.
 *
 * Root-provided so the bell and any future inbox page share one copy, modelled
 * on `core/stores/maintenance.store.ts`. The unread count is polled — there is
 * no WebSocket or SSE anywhere in this app, and every existing "realtime"
 * surface is a `setInterval`.
 *
 * Polling is owned by the component, not the store, so it stops when the header
 * is destroyed rather than running for the lifetime of the tab.
 */
export const NotificationInboxStore = signalStore(
  { providedIn: 'root' },
  withState(initial),
  withComputed(({ items, unread }) => ({
    hasUnread: computed(() => unread() > 0),
    /** Capped for the badge — "99+" is as much as anyone needs to know. */
    badgeCount: computed(() => (unread() > 99 ? '99+' : String(unread()))),
    recent: computed(() => items().slice(0, 10)),
  })),
  withMethods((store, service = inject(NotificationInboxService)) => ({
    refreshUnread(): void {
      service.getUnreadCount().subscribe({
        next: ({ unread }) => patchState(store, { unread: unread ?? 0 }),
        // A failed poll must not clear a count the user can see, and must not
        // raise a toast every interval.
        error: () => {},
      });
    },

    loadInbox(): void {
      patchState(store, { loading: true });
      service.getInbox().subscribe({
        next: (page) =>
          patchState(store, {
            items: page.rows ?? [],
            unread: page.unread ?? 0,
            loading: false,
            loaded: true,
          }),
        error: () => patchState(store, { loading: false, loaded: true }),
      });
    },

    markRead(notificationId: string): void {
      // Optimistic: the badge should drop the instant it is clicked.
      const items = store.items().map((i) =>
        i.notificationId === notificationId && !i.readAt
          ? { ...i, readAt: new Date().toISOString() }
          : i
      );
      patchState(store, { items, unread: Math.max(0, store.unread() - 1) });
      service.markRead(notificationId).subscribe({ error: () => this.refreshUnread() });
    },

    markAllRead(): void {
      const items = store.items().map((i) => (i.readAt ? i : { ...i, readAt: new Date().toISOString() }));
      patchState(store, { items, unread: 0 });
      service.markAllRead().subscribe({ error: () => this.refreshUnread() });
    },

    /**
     * Empty the panel. Optimistic, like markRead — the list should clear on the
     * click, not a round trip later. On failure the server is the authority, so
     * reload rather than guess at what survived.
     */
    dismissAll(): void {
      patchState(store, { items: [], unread: 0, loaded: true });
      service.dismissAll().subscribe({
        // Reload rather than trust the optimistic wipe: the server owns the
        // cleared-through marker, and anything raised between the click and the
        // response should reappear.
        next: () => this.loadInbox(),
        error: () => this.loadInbox(),
      });
    },
  }))
);
